import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RichEditor } from '@/components/rich-editor';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

type Kind = 'terms' | 'privacy' | 'withdrawal';
interface LegalDoc {
  kind: Kind;
  title: string;
  content: string | null;
  url: string | null;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

const HINTS: Record<Kind, string> = {
  terms: 'Ihre Allgemeinen Geschäftsbedingungen, z. B. für Reparaturaufträge.',
  privacy: 'Ihre Datenschutzerklärung. Steht sie schon auf Ihrer Website, reicht der Link.',
  withdrawal: 'Widerrufsbelehrung für Verbraucher (Fernabsatz/Online-Freigabe), am besten mit Muster-Widerrufsformular.',
};

/** Administration → Rechtstexte: Pflicht für die Online-Freigabe von Kostenvoranschlägen. */
export function LegalAdminPage() {
  const { data, isLoading } = useQuery({ queryKey: ['legal'], queryFn: () => api<{ items: LegalDoc[] }>('/legal') });
  const missing = data?.items.filter((d) => !d.content && !d.url) ?? [];
  return (
    <div className="grid max-w-4xl gap-6">
      <PageHeader
        title="Rechtstexte"
        description="AGB, Datenschutzerklärung und Widerrufsbelehrung. Kunden müssen alle drei bestätigen, bevor sie einen Kostenvoranschlag online freigeben; gespeichert wird, welche Fassung bestätigt wurde."
      />
      {missing.length > 0 && (
        <Alert variant="destructive">Online-Freigaben von Kostenvoranschlägen sind gesperrt, bis alle drei Texte hinterlegt sind. Es fehlt: {missing.map((d) => d.title).join(', ')}.</Alert>
      )}
      {isLoading || !data ? <Skeleton className="h-96" /> : data.items.map((d) => <LegalCard key={d.kind} doc={d} />)}
    </div>
  );
}

function LegalCard({ doc }: { doc: LegalDoc }) {
  const qc = useQueryClient();
  const [content, setContent] = useState(doc.content ?? '');
  const [url, setUrl] = useState(doc.url ?? '');
  useEffect(() => {
    setContent(doc.content ?? '');
    setUrl(doc.url ?? '');
  }, [doc.content, doc.url]);
  const save = useMutation({
    mutationFn: () => api<{ items: LegalDoc[] }>(`/legal/${doc.kind}`, { method: 'PUT', body: { content: content || null, url: url.trim() || null } }),
    onSuccess: (d) => {
      qc.setQueryData(['legal'], d);
      toast.success(`${doc.title} gespeichert`);
    },
    onError: (e) => toast.error(errorMessage(e), { duration: 10_000 }),
  });
  const configured = Boolean(doc.content || doc.url);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          {doc.title}
          {configured ? <Badge variant="success">Fassung {doc.version}</Badge> : <Badge variant="outline">nicht hinterlegt</Badge>}
        </CardTitle>
        <CardDescription>
          {HINTS[doc.kind]}
          {doc.updatedAt && ` Zuletzt geändert ${formatDateTime(doc.updatedAt)}${doc.updatedBy ? ` von ${doc.updatedBy}` : ''}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Field label="Link auf Ihre Website (optional)" hint="https://… – wird zusätzlich bzw. statt eines eigenen Texts angezeigt">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.firma.de/agb" inputMode="url" />
        </Field>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium">Eigener Text</span>
          <div className="rounded-md border">
            <RichEditor value={content} onChange={setContent} minHeight="12rem" ariaLabel={doc.title} />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {configured ? (
            <a href={`/legal/${doc.kind}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              So sehen Kunden den Text <ExternalLink className="size-4" />
            </a>
          ) : (
            <span />
          )}
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            <Save /> Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
